import { CacheDetectionResult, CacheSignal } from './logger.types';

/**
 * Класс для определения источника кэширования на основе заголовков HTTP ответа.
 * Анализирует различные сигналы, такие как время ответа, x-cache заголовки и версии Storyblok.
 */
export class CacheDetector {
  // Пороговые значения для определения источника кэша
  private static readonly THRESHOLDS = {
    vercelCache: 0.1, // 100ms
    originRequest: 0.5, // 500ms
    minConfidence: 0.2, // 20% minimum confidence
    highConfidence: 0.8, // 80% for high confidence results
  } as const;

  // Сигналы для определения источника кэша с 'весами'
  private static readonly SIGNALS = {
    vercelCache: [
      { signal: 'Very fast response time', weight: 0.6 },
      { signal: 'x-cache Miss from cloudfront', weight: 0.3 },
      { signal: 'No Storyblok version', weight: 0.1 },
    ],
    storyblokCdn: [
      { signal: 'CloudFront hit in x-cache', weight: 0.5 },
      { signal: 'Has Storyblok version', weight: 0.3 },
      { signal: 'Moderate response time', weight: 0.2 },
    ],
    storyblokOrigin: [
      { signal: 'Cache miss', weight: 0.4 },
      { signal: 'High response time', weight: 0.3 },
      { signal: 'Has origin marker', weight: 0.2 },
      { signal: 'Error from cloudfront in x-cache', weight: 0.1 },
    ],
  } as const;

  /**
   * Основной метод определения источника кэша
   * @param headers - HTTP заголовки response
   * @param isProd - boolean
   */
  public detect(
    headers?: Record<string, string>,
    isProd = false
  ): CacheDetectionResult {
    if (!headers) {
      return this.createUnknownResult();
    }

    if (!isProd) {
      return {
        source: 'STORYBLOK_ORIGIN',
        confidence: 1,
        signals: [
          {
            signal: 'Development environment',
            weight: 1,
            matched: true,
          },
        ],
        metadata: this.parseHeaders(headers),
      };
    }

    const headerInfo = this.parseHeaders(headers);
    const results = this.evaluateAllSources(headerInfo);
    const bestMatch = this.selectBestMatch(results);

    // Если коэффициент достоверности превышает минимальный порог, возвращаем его.
    // В противном случае возвращаем 'unknown cache source'.
    return bestMatch.confidence >= CacheDetector.THRESHOLDS.minConfidence
      ? bestMatch
      : this.createUnknownResult();
  }

  private parseHeaders(headers: Record<string, string>) {
    return {
      xCache: headers['x-cache']?.toLowerCase() ?? '',
      age: parseInt(headers['age'] ?? '0'),
      runtime: parseFloat(headers['x-runtime'] ?? '0'),
      sbBeVersion: headers['sb-be-version'],
      cfId: headers['x-amz-cf-id'],
      xCacheHits: headers['x-cache-hits'],
      originMarker: headers['x-storyblok-origin'],
    };
  }

  /**
   * Оценка всех возможных источников кэша
   */
  private evaluateAllSources(headerInfo: ReturnType<typeof this.parseHeaders>) {
    const evaluations = [
      {
        source: 'VERCEL_CACHE' as const,
        signals: this.evaluateVercelCache(headerInfo),
      },
      {
        source: 'STORYBLOK_CDN' as const,
        signals: this.evaluateStoryblokCdn(headerInfo),
      },
      {
        source: 'STORYBLOK_ORIGIN' as const,
        signals: this.evaluateStoryblokOrigin(headerInfo),
      },
    ];

    return evaluations.map(({ source, signals }) => ({
      source,
      signals,
      confidence: this.calculateConfidence(signals),
      metadata: {
        runtime: headerInfo.runtime,
        age: headerInfo.age,
        xCache: headerInfo.xCache,
        cfId: headerInfo.cfId,
      },
    }));
  }

  private evaluateVercelCache(
    headerInfo: ReturnType<typeof this.parseHeaders>
  ): CacheSignal[] {
    return CacheDetector.SIGNALS.vercelCache.map(({ signal, weight }) => ({
      signal,
      weight,
      matched:
        signal === 'Very fast response time'
          ? headerInfo.runtime < CacheDetector.THRESHOLDS.vercelCache
          : signal === 'x-cache Miss from cloudfront'
          ? headerInfo.xCache.includes('miss from cloudfront')
          : signal === 'No Storyblok version'
          ? !headerInfo.sbBeVersion
          : false,
    }));
  }

  private evaluateStoryblokCdn(
    headerInfo: ReturnType<typeof this.parseHeaders>
  ): CacheSignal[] {
    return CacheDetector.SIGNALS.storyblokCdn.map(({ signal, weight }) => {
      let matched = false;

      switch (signal) {
        case 'CloudFront hit in x-cache':
          matched = headerInfo.xCache.includes('hit from cloudfront');
          break;

        case 'Has Storyblok version':
          matched = !!headerInfo.sbBeVersion;
          break;

        case 'Moderate response time':
          matched =
            headerInfo.runtime >= CacheDetector.THRESHOLDS.vercelCache &&
            headerInfo.runtime < CacheDetector.THRESHOLDS.originRequest;
          break;

        default:
          matched = false;
      }

      return { signal, weight, matched };
    });
  }

  private evaluateStoryblokOrigin(
    headerInfo: ReturnType<typeof this.parseHeaders>
  ): CacheSignal[] {
    return CacheDetector.SIGNALS.storyblokOrigin.map(({ signal, weight }) => ({
      signal,
      weight,
      matched:
        signal === 'Cache miss'
          ? headerInfo.xCache.includes('miss')
          : signal === 'High response time'
          ? headerInfo.runtime > CacheDetector.THRESHOLDS.originRequest
          : signal === 'Has origin marker'
          ? !!headerInfo.originMarker
          : signal === 'Error from cloudfront in x-cache'
          ? headerInfo.xCache.includes('Error from cloudfront')
          : false,
    }));
  }

  /**
   * Расчет коэффециента 'уверенности' на основе совпавших сигналов
   */
  private calculateConfidence(signals: CacheSignal[]): number {
    return signals.reduce(
      (sum, { weight, matched }) => sum + (matched ? weight : 0),
      0
    );
  }

  /**
   * Выбор наиболее вероятного источника кэша
   */
  private selectBestMatch(
    results: CacheDetectionResult[]
  ): CacheDetectionResult {
    return results.reduce((best, current) =>
      current.confidence > best.confidence ? current : best
    );
  }

  /**
   * Создание результата для неопределенного источника
   */
  private createUnknownResult(): CacheDetectionResult {
    return {
      source: 'UNKNOWN',
      confidence: 0,
      signals: [],
      metadata: {},
    };
  }

  public isHighConfidence(result: CacheDetectionResult): boolean {
    return result.confidence >= CacheDetector.THRESHOLDS.highConfidence;
  }
}
